import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { Loader, Select } from '@piggytechng/component-library';
import NoData from '../../../../components/NoData/NoData';
import useCustomers from '../../../../hooks/useCustomers';
import useFlexDollar from '../../../../hooks/useFlexDollar';
import useAccountStatement, {
  STATEMENT_OPTIONS,
  isDollarStatement,
  isPlanStatement,
} from '../../../../hooks/useAccountStatement';
import ModalGenerateStatement from '../../../../components/Modals/ModalGenerateStatement';
import { OverideSelectStyle, formatToDollar, formatToNaira } from '../../../../helpers/utils';
import { GenerateStatementPayload, StatementType, UserBalance } from '../../../../types';
import './SummaryStatementTab.scss';

// `Do` (1st, 2nd, 30th…) in the statement timestamp needs this plugin.
dayjs.extend(advancedFormat);

// Naira balances that make up the account total.
const SUMMARY_ROWS: { key: keyof UserBalance; label: string }[] = [
  { key: 'piggyBank', label: 'Piggybank Wallet' },
  { key: 'flexNaira', label: 'Flex Wallet' },
  { key: 'safeLock', label: 'Total Safelocks' },
  { key: 'targetSavings', label: 'Target Savings' },
  { key: 'houseMoney', label: 'House Money' },
  { key: 'salaryManager', label: 'Salary Manager' },
  { key: 'investify', label: 'Total Investments' },
];

// Piggypoints is a points balance, not money, so it stays out of the naira total.
const NON_NAIRA_ROWS: { key: keyof UserBalance; label: string; format: (val: number) => string }[] = [
  { key: 'piggyPoints', label: 'Piggypoints', format: (val) => val.toLocaleString() },
];

const SummaryStatementTab = () => {
  const [statementType, setStatementType] = useState<StatementType>('summary');
  const { UserBalances, isLoadingUserBalances } = useCustomers();
  const { flexDollarRate } = useFlexDollar();
  const { rows, hasMore, isLoading, isLoadingMore, loadMore, generateAccountStatement, isGeneratingStatement } =
    useAccountStatement(statementType);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const handleGenerate = (payload: GenerateStatementPayload) =>
    generateAccountStatement(payload, () => setShowGenerateModal(false));

  const dollarRate = Number(flexDollarRate?.dollarToNairaRate) || 0;
  const flexDollarBalance = Number(UserBalances?.flexDollar) || 0;
  const flexDollarInNaira = flexDollarBalance * dollarRate;

  const isDollar = isDollarStatement(statementType);
  const isPlans = isPlanStatement(statementType);
  const format = (val: number) => (isDollar ? formatToDollar(val) : formatToNaira(val));

  const activeLabel = STATEMENT_OPTIONS.find((o) => o.value === statementType)?.label ?? 'Statement';

  const total = useMemo(
    () => SUMMARY_ROWS.reduce((sum, row) => sum + (Number(UserBalances?.[row.key]) || 0), 0) + flexDollarInNaira,
    [UserBalances, flexDollarInNaira],
  );

  const walletBalance = useMemo(() => {
    const map: Partial<Record<StatementType, number>> = {
      piggybank: Number(UserBalances?.piggyBank) || 0,
      flexnaira: Number(UserBalances?.flexNaira) || 0,
      flexdollar: Number(UserBalances?.flexDollar) || 0,
      safelock: Number(UserBalances?.safeLock) || 0,
    };
    return map[statementType] ?? 0;
  }, [UserBalances, statementType]);

  const selectStyle = { ...OverideSelectStyle, style: { ...OverideSelectStyle.style, zIndex: undefined } };
  const generatedAt = dayjs().format('dddd Do [of] MMMM YYYY hh:mm:ssA');

  return (
    <div className="summary-statement-tab">
      <div className="summary-statement-tab__controls">
        <Select
          {...selectStyle}
          labelStyle={{ color: '#666766' }}
          backgroundColor="#F9FAFB"
          outlineColor="none"
          height="56px"
          label="Statement Type"
          placeholder="Select statement type"
          options={STATEMENT_OPTIONS}
          value={statementType}
          onChange={(val: StatementType) => setStatementType(val)}
        />

        <button className="summary-statement-tab__generate" onClick={() => setShowGenerateModal(true)}>
          Generate Account Statement
        </button>
      </div>

      <div className="summary-statement-tab__panel">
        <div className="summary-statement-tab__panel-head">
          <h3>{activeLabel}</h3>
          {statementType === 'summary' && dollarRate > 0 && (
            <span className="summary-statement-tab__rate">
              Exchange rate <strong>$1 = ₦{dollarRate.toLocaleString()}</strong>
            </span>
          )}
          {statementType !== 'summary' && (
            <span className="summary-statement-tab__balance">
              Balance: <strong>{isDollar ? formatToDollar(walletBalance) : formatToNaira(walletBalance)}</strong>
            </span>
          )}
        </div>

        {/* ---------------- SUMMARY ---------------- */}
        {statementType === 'summary' &&
          (isLoadingUserBalances ? (
            <div className="page-loader">
              <Loader color="#0F60D6" />
            </div>
          ) : !UserBalances ? (
            <NoData message="No balances available for this user." />
          ) : (
            <div className="summary-statement-tab__table-wrap">
              <table className="summary-statement-tab__table">
                <thead>
                  <tr>
                    <th className="idx">#</th>
                    <th>Title</th>
                    <th className="amount">Amount (₦)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="is-total">
                    <td className="idx">1</td>
                    <td>Total Account Balances at {generatedAt}</td>
                    <td className="amount">{formatToNaira(total)}</td>
                  </tr>
                  {SUMMARY_ROWS.map((row, i) => (
                    <tr key={row.key as string}>
                      <td className="idx">{i + 2}</td>
                      <td>{row.label}</td>
                      <td className="amount">{formatToNaira(Number(UserBalances?.[row.key]) || 0)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="idx">{SUMMARY_ROWS.length + 2}</td>
                    <td>
                      Flex Dollar Wallet{' '}
                      <span className="summary-statement-tab__note">
                        {formatToDollar(flexDollarBalance)}
                        {dollarRate ? ` @ $1 = ₦${dollarRate.toLocaleString()}` : ' — rate unavailable'}
                      </span>
                    </td>
                    <td className="amount">{formatToNaira(flexDollarInNaira)}</td>
                  </tr>
                  {NON_NAIRA_ROWS.map((row, i) => (
                    <tr key={row.key as string} className="is-excluded">
                      <td className="idx">{SUMMARY_ROWS.length + 3 + i}</td>
                      <td>
                        {row.label} <span className="summary-statement-tab__note">not included in total</span>
                      </td>
                      <td className="amount">{row.format(Number(UserBalances?.[row.key]) || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="summary-statement-tab__footnote">
                Flex Dollar is converted to naira at the current rate
                {dollarRate ? ` of $1 = ₦${dollarRate.toLocaleString()}` : ' (currently unavailable)'} and included in
                the total. Piggypoints is a points balance, so it is not added.
              </p>
            </div>
          ))}

        {/* ---------------- PER-WALLET ---------------- */}
        {statementType !== 'summary' &&
          (isLoading ? (
            <div className="page-loader">
              <Loader color="#0F60D6" />
            </div>
          ) : !rows.length ? (
            <NoData message={isPlans ? 'No safelocks at the moment.' : 'No transaction at the moment.'} />
          ) : (
            <>
              <div className="summary-statement-tab__table-wrap">
                <table className="summary-statement-tab__table">
                  <thead>
                    <tr>
                      <th className="idx">#</th>
                      <th className="date">Date</th>
                      <th>{isPlans ? 'Title' : 'Memo'}</th>
                      <th className="amount">{`Amount (${isDollar ? '$' : '₦'})`}</th>
                      <th className="amount">
                        {isPlans ? `Interest (${isDollar ? '$' : '₦'})` : `Balance (${isDollar ? '$' : '₦'})`}
                      </th>
                      {isPlans && <th className="status">Status</th>}
                      <th>{isPlans ? 'Safelock ID' : 'Reference'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={`${row.id}-${i}`}>
                        <td className="idx">{i + 1}</td>
                        <td className="date">
                          {row.date ? dayjs.unix(row.date).format('D MMM YYYY | h:mm A') : '-----'}
                        </td>
                        <td className="title">{row.title}</td>
                        <td className="amount">{format(row.amount)}</td>
                        <td className="amount">{format(row.balance ?? 0)}</td>
                        {isPlans && <td className="status">{row.status ?? '-----'}</td>}
                        <td className="reference">{row.reference || '-----'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className="load-more" onClick={loadMore}>
                  {isLoadingMore ? (
                    <Loader color="#0F60D6" maxWidth="50px" />
                  ) : (
                    <span className="pointer">Show more</span>
                  )}
                </div>
              )}
            </>
          ))}
      </div>

      <ModalGenerateStatement
        modalIsOpen={showGenerateModal}
        setIsOpen={setShowGenerateModal}
        onSubmit={handleGenerate}
        isLoading={isGeneratingStatement}
      />
    </div>
  );
};

export default SummaryStatementTab;
